import { inject, type Signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { IncidentStatus, IncidentsV2Service } from '@api/incidents';
import { NavigationService, queryParamUtils } from '@core';
import { catchError, map, of, switchMap } from 'rxjs';

import { buildIncidentsFilters } from '../components/tables/incidents-v2-table/incidents-v2-table.store';
import { type IncidentsScope } from './incidents-criteria.store';

export interface OpenIncidentCounts {
  criticalOrMajor: number;
  minor: number;
}

/* Same filters as the Open tab of the v2 table, so the toggle badges always match the list below. Must run in an injection context. */
export function injectOpenIncidentCounts(scope: Signal<IncidentsScope>) {
  const incidentsV2Service = inject(IncidentsV2Service);
  const navigationService = inject(NavigationService);

  return toSignal(
    toObservable(scope).pipe(
      switchMap(currentScope => {
        const filters = buildIncidentsFilters({
          ...currentScope,
          deviceStatus: ['active', 'paused'],
          status: [IncidentStatus.INPROGRESS, IncidentStatus.NEW],
        });

        return incidentsV2Service
          .getStatistics({ clientId: navigationService.getCurrentClientId() }, { filters: queryParamUtils.toJsonQueryParam(filters) })
          .pipe(
            map(({ data }): OpenIncidentCounts => ({ criticalOrMajor: data.critical + data.major, minor: data.minor })),
            catchError(() => of<OpenIncidentCounts>({ criticalOrMajor: 0, minor: 0 })),
          );
      }),
    ),
    { initialValue: { criticalOrMajor: 0, minor: 0 } satisfies OpenIncidentCounts },
  );
}
